import { useState, useRef, useEffect } from 'react';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { ReactComponent as ArrowIcon } from './assets/arrow.svg';
import { ReactComponent as ReportIcon } from './assets/report.svg';
import styles from './CopilotButtonAssistantUI.module.scss';

const EmptyState = ({ t, onSelectTemplate, context = 'case', onShowPublications }) => {
  const [currentUser] = useCurrentUser();
  const [reportOpen, setReportOpen] = useState(false);
  const dropdownRef = useRef(null);

  const CASE_SUGGESTED = [
  { id: 1, label: t('Provide a summary of this case'), query: t('Provide a summary of this case') },
  { id: 2, label: t('What are the top 5 threatening narratives in this case?'), query: t('What are the top 5 threatening narratives in this case?') },
  { id: 3, label: t('What is the geographic distribution of the most viral topic?'), query: t('What is the geographic distribution of the most viral topic?') },
  { id: 4, label: t('Generate a basic PDF report on this case'), query: t('Generate a basic PDF report on this case') },
  // { id: 5, label: 'Show me a trend chart of publications over time', query: 'Show me a trend chart of publications over time', adminOnly: true },
  // { id: 6, label: 'Browse linked publications', query: null, action: 'publications', adminOnly: true },
];

const DASHBOARD_SUGGESTED = [
  { id: 1, label: t('Make me a summary of this dashboard'), query: t('Make me a summary of this dashboard') },
  { id: 2, label: t('What are key points about this dashboard?'), query: t('What are key points about this dashboard?') },
  { id: 3, label: t('Show me top 5 narratives this dashboard contains'), query: t('Show me top 5 narratives this dashboard contains') },
  // { id: 4, label: 'Show me a trend chart of publications over time', query: 'Show me a trend chart of publications over time', adminOnly: true },
];

const REPORT_TEMPLATES = [
  { id: 1, label: t('Case report'), subtitle: t('Generate a comprehensive report for this case') },
];

  const isSuperAdmin = currentUser?.is_super_admin === true;
  const allSuggestions = context === 'dashboard' ? DASHBOARD_SUGGESTED : CASE_SUGGESTED;
  const suggestions = isSuperAdmin ? allSuggestions : allSuggestions.filter((s) => !s.adminOnly);
  const headline =
    context === 'dashboard'
      ? t('Ask me anything about the data on this dashboard')
      : t('Ask me anything about the data on this case');

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setReportOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.emptyState} role="region" aria-label={t('Suggested queries')}>
      <div className={styles.reportTemplatesArea} ref={dropdownRef}>
        <button
          type="button"
          className={styles.reportTemplatesBtn}
          onClick={() => setReportOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={reportOpen}
        >
          <ReportIcon aria-hidden="true" />
          {t('Report templates')}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        {reportOpen && (
          <ul
            className={styles.reportDropdown}
            role="listbox"
            aria-label={t('Report templates')}
          >
            {REPORT_TEMPLATES.map((tpl) => (
              <li
                key={tpl.id}
                role="option"
                aria-selected="false"
                className={styles.reportDropdownItem}
                tabIndex={0}
                onClick={() => {
                  onSelectTemplate(t('Generate case report'), { report: true });
                  setReportOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectTemplate(t('Generate case report'), { report: true });
                    setReportOpen(false);
                  }
                }}
              >
                <ReportIcon className={styles.reportDropdownItemIcon} aria-hidden="true" />
                <div className={styles.reportDropdownItemText}>
                  <span className={styles.reportDropdownItemTitle}>{t(tpl.label)}</span>
                  <span className={styles.reportDropdownItemSubtitle}>{t(tpl.subtitle)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.emptyContent}>
        <h2 className={styles.emptyHeadline}>{headline}</h2>
        <p className={styles.suggestedLabel}>{t('Suggested')}</p>
        <ul className={styles.suggestedList}>
          {suggestions.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className={styles.suggestedPill}
                onClick={() => s.action === 'publications' ? onShowPublications?.() : onSelectTemplate(s.query)}
              >
                <ArrowIcon aria-hidden="true" />
                {t(s.label)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default EmptyState;
